import React, { useState, useEffect, useRef } from 'react';
import { Bell, Check, CheckCheck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '@/services/api';
import { cn } from '@/utils/cn';

interface InAppNotification {
  id: number;
  type: string;
  subject: string;
  message: string;
  read: boolean;
  created_at: string;
  reconciliation_id?: number;
  period_id?: number;
  context?: Record<string, unknown>;
  variant: 'error' | 'success' | 'warning' | 'info' | 'default';
}

export const NotificationBell: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState<InAppNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isEnabled, setIsEnabled] = useState<boolean | null>(null); // null = loading
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Check if in-app notifications are enabled
  useEffect(() => {
    const checkPreference = async () => {
      try {
        const response = await api.get('/api/notifications/preferences');
        const prefs = response.data?.preferences || [];
        // Check any preference for in_app_enabled (they should all be the same)
        const anyPref = prefs.find((p: any) => p.notification_type === 'rejection');
        setIsEnabled(anyPref?.in_app_enabled ?? true);
      } catch (error) {
        // Default to enabled if we can't fetch preferences
        setIsEnabled(true);
      }
    };
    checkPreference();
  }, []);

  // Fetch unread count periodically (only if enabled)
  useEffect(() => {
    if (isEnabled === false) return;

    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000); // Every 30 seconds
    return () => clearInterval(interval);
  }, [isEnabled]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchUnreadCount = async () => {
    try {
      const response = await api.get('/api/notifications/in-app/unread-count');
      setUnreadCount(response.unread_count || 0);
    } catch (error) {
      console.error('Failed to fetch unread count:', error);
    }
  };

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/notifications/in-app', {
        params: { unread_only: false, limit: 20 }
      });
      setNotifications(response.notifications || []);
      setUnreadCount(response.unread_count || 0);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBellClick = () => {
    if (!isOpen) {
      fetchNotifications();
    }
    setIsOpen(!isOpen);
  };

  const markAsRead = async (id: number) => {
    try {
      await api.post(`/api/notifications/in-app/${id}/read`);
      setNotifications(prev =>
        prev.map(n => n.id === id ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  const markAllRead = async () => {
    try {
      await api.post('/api/notifications/in-app/mark-all-read');
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  const handleNotificationClick = (notification: InAppNotification) => {
    if (!notification.read) {
      markAsRead(notification.id);
    }

    // Navigate to relevant page
    if (notification.reconciliation_id) {
      navigate(`/reconciliation/${notification.reconciliation_id}`);
      setIsOpen(false);
    }
  };

  const getVariantStyles = (variant: string, read: boolean) => {
    if (read) return '';

    const styles: Record<string, string> = {
      error: 'border-l-4 border-red-500 bg-red-50',
      success: 'border-l-4 border-green-500 bg-green-50',
      warning: 'border-l-4 border-yellow-500 bg-yellow-50',
      info: 'border-l-4 border-blue-500 bg-blue-50',
      default: 'border-l-4 border-gray-300 bg-gray-50'
    };
    return styles[variant] || styles.default;
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  // Don't render if disabled or still loading preference
  if (isEnabled === false || isEnabled === null) {
    return null;
  }

  return (
    <div className="relative notification-bell" ref={dropdownRef}>
      {/* Bell Button */}
      <button
        onClick={handleBellClick}
        className={cn(
          "relative p-2 rounded-full transition-colors",
          "text-gray-300 hover:text-white hover:bg-gray-700",
          "focus:outline-none focus:ring-2 focus:ring-white"
        )}
        aria-label="Notifications"
      >
        <Bell className="h-5 w-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-xs font-bold leading-none text-white bg-red-500 rounded-full">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-96 bg-white rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 z-50">
          {/* Header */}
          <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">Notifications</h3>
            {unreadCount > 0 && (
              <button
                onClick={markAllRead}
                className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
              >
                <CheckCheck className="h-4 w-4" />
                Mark all read
              </button>
            )}
          </div>

          {/* Notifications List */}
          <div className="max-h-96 overflow-y-auto">
            {loading ? (
              <div className="p-4 text-center text-gray-500">Loading...</div>
            ) : notifications.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <Bell className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                <p>No notifications</p>
              </div>
            ) : (
              notifications.map(notification => (
                <div
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  className={cn(
                    "px-4 py-3 cursor-pointer hover:bg-gray-50 transition-colors",
                    getVariantStyles(notification.variant, notification.read)
                  )}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <p className={cn(
                        "text-sm text-gray-900",
                        !notification.read && "font-semibold"
                      )}>
                        {notification.message}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {formatTime(notification.created_at)}
                      </p>
                    </div>
                    {!notification.read && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          markAsRead(notification.id);
                        }}
                        className="ml-2 text-gray-400 hover:text-gray-600"
                        title="Mark as read"
                      >
                        <Check className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          <div className="px-4 py-3 border-t border-gray-200">
            <button
              onClick={() => {
                navigate('/notifications/history');
                setIsOpen(false);
              }}
              className="text-sm text-blue-600 hover:text-blue-800 w-full text-center"
            >
              View all notifications
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
